/** @format */

import { useEffect, useRef } from "react";
import type { Question } from "@question-bank/shared";
import { toStringArray } from "../utils/helpers";

interface DeleteConfirmModalProps {
	question: Question;
	/**
	 * Subject(s) of the owning Paper, for the confirmation summary. Paper-level
	 * facts no longer live on a bare Question (QB2-223); the caller resolves the
	 * Paper and passes the subject in. Optional — the row falls back to "-".
	 */
	subject?: string | string[];
	isDeleting: boolean;
	error: string | null;
	onConfirm: () => void;
	onCancel: () => void;
}

export const DeleteConfirmModal = ({
	question,
	subject: subjectProp,
	isDeleting,
	error,
	onConfirm,
	onCancel,
}: DeleteConfirmModalProps) => {
	const cancelRef = useRef<HTMLButtonElement>(null);
	const modalRef = useRef<HTMLDivElement>(null);

	// Focus cancel on open
	useEffect(() => {
		cancelRef.current?.focus();
	}, []);

	// Escape to close modal
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape" && !isDeleting) {
				onCancel();
			}
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onCancel, isDeleting]);

	// Focus trap
	useEffect(() => {
		const modal = modalRef.current;
		if (!modal) {
			return;
		}
		const focusable = modal.querySelectorAll<HTMLElement>(
			'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
		);
		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const trap = (e: KeyboardEvent) => {
			if (e.key !== "Tab") {
				return;
			}
			if (e.shiftKey ? document.activeElement === first : document.activeElement === last) {
				e.preventDefault();
				(e.shiftKey ? last : first).focus();
			}
		};
		modal.addEventListener("keydown", trap);
		return () => modal.removeEventListener("keydown", trap);
	}, [isDeleting]);

	const subject = toStringArray(subjectProp).join(", ") || "-";

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
			<div
				ref={modalRef}
				role="alertdialog"
				aria-modal="true"
				aria-labelledby="delete-modal-title"
				aria-describedby="delete-modal-desc"
				className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-lg"
			>
				<h2 id="delete-modal-title" className="text-base font-semibold text-slate-800">
					Delete question ?
				</h2>

				<p id="delete-modal-desc" className="mt-2 text-sm text-slate-600">
					This will permanently delete the following question. This action cannot be undone.
				</p>

				<dl className="mt-3 space-y-1 rounded-md bg-slate-50 px-4 py-3 text-sm">
					<div className="flex gap-2">
						<dt className="w-28 shrink-0 font-medium text-slate-500">Subject</dt>
						<dd className="text-slate-800">{subject}</dd>
					</div>
					<div className="flex gap-2">
						<dt className="w-28 shrink-0 font-medium text-slate-500">Question no.</dt>
						<dd className="text-slate-800">{question.questionNumber ?? "-"}</dd>
					</div>
					<div className="flex gap-2">
						<dt className="w-28 shrink-0 font-medium text-slate-500">Paper code</dt>
						<dd className="text-slate-800">{question.paperCode ?? "-"}</dd>
					</div>
				</dl>

				{error && (
					<p role="alert" className="mt-3 text-sm font-medium text-red-600">
						{error}
					</p>
				)}

				<div className="mt-5 flex justify-end gap-3">
					<button
						ref={cancelRef}
						type="button"
						onClick={onCancel}
						className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={onConfirm}
						disabled={isDeleting}
						className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
					>
						{isDeleting ? "Deleting..." : "Confirm Delete"}
					</button>
				</div>
			</div>
		</div>
	);
};
